import { useBusinessSubscription } from '@/api';
import { UpgradePlanModal } from '@/components/molecules/upgrade-plan-modal';
import {
  isWithinLimit,
  resolvePlanEntitlements,
} from '@/lib/billing/plan-entitlements';
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import {
  fallbackEntitlements,
  PlanGateContext,
  type BooleanFeature,
  type CountFeature,
  type PlanUpgradePrompt,
} from './plan-gate-context';

function formatLimit(limit: number) {
  return Number.isFinite(limit) ? limit.toLocaleString() : 'unlimited';
}

function promptForFeature(
  feature: BooleanFeature | CountFeature,
  entitlements: ReturnType<typeof resolvePlanEntitlements>
): PlanUpgradePrompt {
  switch (feature) {
    case 'imagesPerProduct':
      return {
        title: 'More images need a higher plan',
        description: `Your current plan includes ${formatLimit(entitlements.maxImagesPerProduct)} image${entitlements.maxImagesPerProduct === 1 ? '' : 's'} per product. Upgrade to add more photos to this product.`,
      };
    case 'productUploads':
      return {
        title: 'You have reached your product limit',
        description: `Your current plan includes up to ${formatLimit(entitlements.maxProducts)} products. Upgrade to add more to your catalog.`,
      };
    case 'bulkUpload':
      return {
        title: 'Bulk upload is not on your plan',
        description:
          'Your current plan does not include spreadsheet uploads. Upgrade to add many products at once.',
      };
    case 'analytics':
      return {
        title: 'Analytics is not on your plan',
        description:
          'Your current plan does not include store analytics. Upgrade to see how your sales and products are performing.',
      };
    case 'leadManagement':
      return {
        title: 'Lead management is not on your plan',
        description:
          'Your current plan does not include lead scoring and follow-ups. Upgrade to manage hotter leads from your inbox.',
      };
    case 'aiConversations':
      return {
        title: 'AI replies are not on your plan',
        description:
          'Your current plan does not include AI conversations. Upgrade so Sellyt can draft replies to your customers.',
      };
    case 'customDomain':
      return {
        title: 'Custom domains need a higher plan',
        description:
          'Custom domains are available on Pro and Scale as a paid add-on. Upgrade your plan, then purchase a domain through Sellyt.',
      };
  }
}

export function PlanGateProvider({ children }: { children: ReactNode }) {
  const { data: subscription, isFetched } = useBusinessSubscription();
  const [prompt, setPrompt] = useState<PlanUpgradePrompt | null>(null);

  const entitlements = useMemo(
    () =>
      isFetched ? resolvePlanEntitlements(subscription) : fallbackEntitlements,
    [isFetched, subscription]
  );

  const openUpgradeModal = useCallback((nextPrompt?: PlanUpgradePrompt) => {
    setPrompt(
      nextPrompt ?? {
        title: 'This is not included in your plan',
        description:
          'Upgrade your subscription to use this feature. You can compare plans and pick the one that fits how you sell.',
      }
    );
  }, []);

  const allowsFeature = useCallback(
    (feature: BooleanFeature) => {
      if (!isFetched) return true;

      const allowed =
        feature === 'bulkUpload'
          ? entitlements.bulkUpload
          : feature === 'analytics'
            ? entitlements.analytics !== false
            : feature === 'leadManagement'
              ? entitlements.leadManagement
              : feature === 'customDomain'
                ? entitlements.customDomain
                : entitlements.maxAiConversationsPerMonth > 0;

      if (!allowed) openUpgradeModal(promptForFeature(feature, entitlements));
      return allowed;
    },
    [entitlements, isFetched, openUpgradeModal]
  );

  const allowsCount = useCallback(
    (feature: CountFeature, nextCount: number) => {
      if (!isFetched) return true;

      const limit =
        feature === 'imagesPerProduct'
          ? entitlements.maxImagesPerProduct
          : entitlements.maxProducts;
      const allowed = isWithinLimit(limit, nextCount);
      if (!allowed) openUpgradeModal(promptForFeature(feature, entitlements));
      return allowed;
    },
    [entitlements, isFetched, openUpgradeModal]
  );

  const value = useMemo(
    () => ({
      isReady: isFetched,
      entitlements,
      allowsFeature,
      allowsCount,
      openUpgradeModal,
    }),
    [allowsCount, allowsFeature, entitlements, isFetched, openUpgradeModal]
  );

  return (
    <PlanGateContext.Provider value={value}>
      {children}
      <UpgradePlanModal
        open={!!prompt}
        title={prompt?.title}
        description={prompt?.description}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPrompt(null);
        }}
      />
    </PlanGateContext.Provider>
  );
}
